import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import type { CalendarBlockedDate, CalendarBooking, CalendarCleaning, CalendarEvent as CalendarEventType, MissingCleaning } from '../types/calendar';
import CalendarEvent from './CalendarEvent';

interface CalendarDayProps {
    date: DateTime;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isMobile: boolean;
    events: CalendarEventType[];
    onDayClick: (date: string) => void;
    onBookingClick: (booking: CalendarBooking, anchorEl: HTMLElement) => void;
    onBlockedDateClick: (blockedDate: CalendarBlockedDate) => void;
    onCleaningClick: (cleaning: CalendarCleaning) => void;
    onMissingCleaningClick: (missingCleaning: MissingCleaning) => void;
}

const CalendarDay = ({
    date,
    isCurrentMonth,
    isToday,
    isSelected,
    isMobile,
    events,
    onDayClick,
    onBookingClick,
    onBlockedDateClick,
    onCleaningClick,
    onMissingCleaningClick,
}: CalendarDayProps) => {
    const dateStr = date.toFormat('yyyy-MM-dd');

    // Get events that should be displayed on this day
    const dayEvents = events.filter((event) => {
        return dateStr >= event.startDate && dateStr <= event.endDate;
    });

    // Sort events by start date for consistent stacking
    const sortedEvents = [...dayEvents].sort((a, b) => {
        if (a.startDate !== b.startDate) {
            return a.startDate.localeCompare(b.startDate);
        }
        // Same start date - bookings before blocked dates
        if (a.type === 'booking' && a.type !== b.type) return -1;
        if (b.type === 'booking' && a.type !== b.type) return 1;
        return a.id - b.id;
    });

    // Limit visible events (2 on mobile, 3 on desktop)
    const maxVisibleEvents = isMobile ? 2 : 3;
    const visibleEvents = sortedEvents.slice(0, maxVisibleEvents);
    const hiddenCount = sortedEvents.length - maxVisibleEvents;

    const handleDayClick = () => {
        onDayClick(dateStr);
    };

    const handleEventClick = (event: CalendarEventType, e: React.MouseEvent) => {
        e.stopPropagation();
        if (event.type === 'booking' && event.booking) {
            onBookingClick(event.booking, e.currentTarget as HTMLElement);
        } else if (event.type === 'cleaning' && event.cleaning) {
            onCleaningClick(event.cleaning);
        } else if (event.type === 'missing-cleaning' && event.missingCleaning) {
            onMissingCleaningClick(event.missingCleaning);
        } else if (event.blockedDate) {
            onBlockedDateClick(event.blockedDate);
        }
    };

    return (
        <Box
            onClick={handleDayClick}
            sx={{
                minHeight: isMobile ? '60px' : '100px',
                border: isToday ? '2px solid #6366f1' : '1px solid #374151',
                backgroundColor: isSelected
                    ? 'rgba(99, 102, 241, 0.2)'
                    : isCurrentMonth
                      ? '#1a1a2e'
                      : '#0f0f23',
                opacity: isCurrentMonth ? 1 : 0.5,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                    backgroundColor: isSelected
                        ? 'rgba(99, 102, 241, 0.3)'
                        : 'rgba(99, 102, 241, 0.1)',
                },
            }}
        >
            <Typography
                sx={{
                    p: isMobile ? 0.25 : 0.5,
                    fontWeight: isToday ? 900 : 600,
                    fontSize: isMobile ? '0.7rem' : '0.85rem',
                    color: isToday ? '#6366f1' : isCurrentMonth ? '#fff' : '#6b7280',
                }}
            >
                {date.day}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    pb: 0.5,
                }}
            >
                {visibleEvents.map((event) => {
                    const isStart = event.startDate === dateStr;
                    const isEnd = event.endDate === dateStr;

                    return (
                        <Box
                            key={`${event.type}-${event.id}`}
                            onClick={(e) => handleEventClick(event, e)}
                        >
                            <CalendarEvent
                                type={event.type}
                                label={event.label}
                                isStart={isStart}
                                isEnd={isEnd}
                                isMobile={isMobile}
                                booking={event.booking}
                                blockedDate={event.blockedDate}
                                cleaning={event.cleaning}
                                missingCleaning={event.missingCleaning}
                            />
                        </Box>
                    );
                })}
                {hiddenCount > 0 && (
                    <Typography
                        sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: '#9ca3af',
                            px: 0.5,
                        }}
                    >
                        +{hiddenCount} more
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default CalendarDay;
